import { css, html, LitElement } from "lit";

import { ifDefined } from "lit/directives/if-defined.js";

import { getAssetUrl } from "helpers/assetHelpers";
import { getMediaMimeTypeFromUrl } from "helpers/getMediaMimeTypeFromUrl";
import { customElement, property, query, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhSpeakerHigh";
import "@phosphor-icons/webcomponents/PhSpeakerSlash";

@customElement("image-or-video")
export class ImageOrVideo extends LitElement {
  static override styles = [
    css`
      video,
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        z-index: 0;
        pointer-events: none;
      }

      .speaker-icon-button {
        position: absolute;
        top: var(--sds-size-space-2400);
        right: var(--sds-size-space-300);

        display: inline-flex;
        justify-content: center;
        align-items: center;

        width: var(--sds-size-icon-large);
        height: var(--sds-size-icon-large);
        border-radius: var(--sds-size-radius-full);
        border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
        background: var(--sds-color-background-neutral-secondary);
        color: var(--sds-color-icon-default-default);
        cursor: pointer;
        appearance: none;
        outline: none;
      }
    `,
  ];

  @property({ type: String })
  url = "";

  @property({ type: String })
  alt?: string;

  @state() muted = true;

  @query("video") videoElement!: HTMLVideoElement;

  private handleVideoMute() {
    this.muted = !this.muted;
    if (this.videoElement) this.videoElement.muted = this.muted;
  }

  override render() {
    const type = getMediaMimeTypeFromUrl(this.url);

    const assetUrl = getAssetUrl(this.url);
    if (type?.kind === "image") {
      return html`<img src=${assetUrl} alt=${this.alt ?? "highlighted post image"} />`;
    }
    return html`
      <video
        loop
        muted
        playsinline
        preload="auto"
        src=${assetUrl}
        @loadeddata=${(e: Event) => {
          const video = e.target as HTMLVideoElement;
          video.play().catch((err) => console.error("Video play failed:", err));
        }}
        @error=${(e: Event) => console.error("Video error:", (e.target as HTMLVideoElement)?.error)}
      >
        Your browser does not support the video tag.
      </video>
      <div
        class="speaker-icon-button"
        aria-pressed="${this.muted ? "true" : "false"}"
        title="${this.muted ? "Unmute" : "Mute"}"
        @click=${this.handleVideoMute}
      >
        ${this.muted
          ? html`<ph-speaker-slash size="20px"></ph-speaker-slash>`
          : html`<ph-speaker-high size="20px"></ph-speaker-high>`}
      </div>
    `;
  }
}

export function renderImageOrVideo(url: string, alt?: string, cssClass?: string) {
  const type = getMediaMimeTypeFromUrl(url);

  const assetUrl = getAssetUrl(url);

  if (type?.kind == "image") {
    return html`<img src=${assetUrl} alt=${alt ?? "highlighted post image"} class=${ifDefined(cssClass)} />`;
  } else {
    return html`
      <video
        class=${ifDefined(cssClass)}
        loop
        muted
        playsinline
        preload="auto"
        src=${assetUrl}
        @loadeddata=${(e: Event) => {
          const video = e.target as HTMLVideoElement;
          video.play().catch((err) => console.error("Video play failed:", err));
        }}
        @error=${(e: Event) => console.error("Video error:", (e.target as HTMLVideoElement)?.error)}
      >
        Your browser does not support the video tag.
      </video>
    `;
  }
}

export function getFirstFrameDataURL(videoUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.src = videoUrl;
    video.crossOrigin = "anonymous"; // Optional: if using remote videos
    video.preload = "metadata";
    video.muted = true; // Required for some browsers to allow playback
    video.playsInline = true; // Mobile compatibility

    // Hide the video element
    video.style.display = "none";
    document.body.appendChild(video);

    const cleanup = () => {
      video.pause();
      URL.revokeObjectURL(video.src);
      document.body.removeChild(video);
    };

    video.addEventListener("loadeddata", () => {
      video.currentTime = 0;
    });

    video.addEventListener("seeked", () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        cleanup();
        reject(new Error("Canvas 2D context is not available."));
        return;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frameDataURL = canvas.toDataURL("image/png");

      cleanup();
      resolve(frameDataURL);
    });

    video.addEventListener("error", () => {
      cleanup();
      reject(new Error("Failed to load video."));
    });
  });
}

export async function getPreviewUrl(url: string) {
  const type = getMediaMimeTypeFromUrl(url);
  if (type?.kind === "image") {
    return getAssetUrl(url);
  } else {
    return await getFirstFrameDataURL(getAssetUrl(url));
  }
}
